import {CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL} from "../../api/model/common";
import {ActivitiesEmissionsResponse} from "../../api/model/emissions";

/**
 * Evaluates whether all activities in the provided calculated activities list were successfully calculated.
 *
 * @param {ActivitiesEmissionsResponse | null} calculatedActivities - An array containing the emissions response for activities or null.
 * @returns {boolean | null} - Returns `true` if all activities have a successful status, `false` if any activity has failed,
 *                             or `null` if the input is `null`.
 */
export const allActivitiesCalculatedSuccessful = (calculatedActivities: ActivitiesEmissionsResponse | null): boolean | null => {
    if (calculatedActivities === null) return null
    return calculatedActivities.every(calculatedActivity => {
        const {status} = calculatedActivity;
        switch (status) {
            case CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.success:
                return true;
            case CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.failed:
                return false;
            default:
                status satisfies never;
        }
    })
}

/**
 * Determines whether all activities are calculated with errors from the given calculated activities data.
 *
 * @param {ActivitiesEmissionsResponse | null} calculatedActivities - The activities emissions response object or null.
 * @returns {boolean | null} - Returns `true` if all activities are calculated with errors, `false` if at least one activity is successfully calculated, or `null` if the input is null.
 */
export const allActivitiesCalculatedWithError = (calculatedActivities: ActivitiesEmissionsResponse | null): boolean | null => {
    if (calculatedActivities === null) return null
    return calculatedActivities.every(calculatedActivity => {
        const {status} = calculatedActivity;
        switch (status) {
            case CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.success:
                return false;
            case CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.failed:
                return true;
            default:
                status satisfies never;
        }
    })
}

/**
 * Checks whether at least one activity in the provided list of calculated activities
 * has encountered an error during its calculation process.
 *
 * @param {ActivitiesEmissionsResponse | null} calculatedActivities - The response object containing
 * activities and their emission statuses, or `null` if no activities are available.
 * @returns {boolean | null} - Returns `true` if at least one activity has a status of failure,
 * `false` if all activities have succeeded, or `null` if the input is `null`.
 */
export const oneActivityCalculatedWithError = (calculatedActivities: ActivitiesEmissionsResponse | null): boolean | null => {
    if (calculatedActivities === null) return null
    return calculatedActivities.some(calculatedActivity => {
        const {status} = calculatedActivity;
        switch (status) {
            case CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.success:
                return false
            case CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.failed:
                return true
            default:
                status satisfies never
        }
    })
}

